import { useState } from 'react';
import { useStore, selectCloudUser, selectCloudSyncStatus, selectCloudLastSyncAt } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { getCloudSyncService } from '@/services/cloud-sync';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';

export function CloudSection() {
  const t = useT();
  const cloudEnabled = useStore((s) => s.settings.cloud.enabled);
  const setCloudEnabled = useStore((s) => s.setCloudEnabled);
  const user = useStore(selectCloudUser);
  const status = useStore(selectCloudSyncStatus);
  const lastSyncAt = useStore(selectCloudLastSyncAt);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const svc = getCloudSyncService();
  const available = svc.isAvailable();

  if (!available) {
    return (
      <div style={section}>
        <h3 style={titleStyle}>☁️ {t('cloud.title')}</h3>
        <p style={mutedText}>{t('cloud.unavailable')}</p>
      </div>
    );
  }

  const onSendMagicLink = async (): Promise<void> => {
    if (!email) return;
    setBusy(true);
    const r = await svc.signInWithEmail(email);
    setBusy(false);
    setMessage(r.ok ? t('cloud.magicLink.sent') : `${t('cloud.error')}: ${r.error}`);
  };

  const onSignOut = async (): Promise<void> => {
    setBusy(true);
    await svc.signOut();
    setBusy(false);
    setMessage(t('cloud.signedOut'));
  };

  const onSyncNow = async (): Promise<void> => {
    setBusy(true);
    const r = await svc.pushNow();
    setBusy(false);
    setMessage(r.ok ? t('cloud.synced') : `${t('cloud.error')}: ${r.error}`);
  };

  return (
    <div style={section}>
      <h3 style={titleStyle}>☁️ {t('cloud.title')}</h3>
      <label style={toggleRow}>
        <input
          type="checkbox"
          data-testid="cloud-toggle"
          checked={cloudEnabled}
          onChange={(e) => setCloudEnabled(e.target.checked)}
        />
        <span>{t('cloud.enable')}</span>
      </label>

      {cloudEnabled && !user && (
        <div style={loginRow}>
          <input
            type="email"
            data-testid="cloud-email"
            placeholder={t('cloud.email.placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
          <button
            data-testid="cloud-send-link"
            disabled={busy || !email}
            onClick={() => void onSendMagicLink()}
            style={primaryBtn}
          >
            {t('cloud.magicLink.send')}
          </button>
        </div>
      )}

      {cloudEnabled && user && (
        <div style={statusBox}>
          <p style={mutedText}>
            {t('cloud.loggedAs')}: <strong style={{ color: COLORS.accent }}>{user.email}</strong>
          </p>
          <p style={mutedText}>
            {t('cloud.status')}: <span style={statusColor(status)}>{t(`cloud.status.${status}`)}</span>
          </p>
          {lastSyncAt && (
            <p style={mutedText}>
              {t('cloud.lastSync')}: {new Date(lastSyncAt).toLocaleTimeString()}
            </p>
          )}
          <div style={{ display: 'flex', gap: SPACING.sm }}>
            <button
              data-testid="cloud-sync-now"
              disabled={busy}
              onClick={() => void onSyncNow()}
              style={primaryBtn}
            >
              {t('cloud.syncNow')}
            </button>
            <button
              data-testid="cloud-sign-out"
              disabled={busy}
              onClick={() => void onSignOut()}
              style={secondaryBtn}
            >
              {t('cloud.signOut')}
            </button>
          </div>
        </div>
      )}

      {message && <p style={messageStyle}>{message}</p>}
    </div>
  );
}

const section: React.CSSProperties = {
  marginTop: SPACING.lg,
  padding: SPACING.md,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  background: COLORS.surfaceAlt,
};
const titleStyle: React.CSSProperties = {
  margin: 0,
  marginBottom: SPACING.sm,
  fontSize: FONT.sizeMd,
  color: COLORS.accent,
};
const mutedText: React.CSSProperties = {
  fontSize: FONT.sizeXs,
  color: COLORS.textDim,
  margin: 0,
  marginBottom: SPACING.xs,
};
const toggleRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.sm,
  fontSize: FONT.sizeSm,
  color: COLORS.text,
  cursor: 'pointer',
  marginBottom: SPACING.sm,
};
const loginRow: React.CSSProperties = {
  display: 'flex',
  gap: SPACING.sm,
  marginTop: SPACING.sm,
};
const inputStyle: React.CSSProperties = {
  flex: 1,
  background: COLORS.surface,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.sm,
  padding: SPACING.sm,
  fontSize: FONT.sizeSm,
  fontFamily: FONT.family,
};
const primaryBtn: React.CSSProperties = {
  background: COLORS.accent,
  color: '#000',
  border: 'none',
  borderRadius: RADIUS.sm,
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: FONT.sizeXs,
  fontWeight: 600,
};
const secondaryBtn: React.CSSProperties = {
  background: 'transparent',
  color: COLORS.textDim,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.sm,
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: FONT.sizeXs,
};
const statusBox: React.CSSProperties = {
  marginTop: SPACING.sm,
};
const messageStyle: React.CSSProperties = {
  marginTop: SPACING.sm,
  padding: SPACING.sm,
  background: 'rgba(108,240,143,0.1)',
  color: COLORS.ok,
  borderRadius: RADIUS.sm,
  fontSize: FONT.sizeXs,
  border: `1px solid ${COLORS.ok}33`,
};
function statusColor(status: string): React.CSSProperties {
  let color: string = COLORS.textDim;
  if (status === 'synced') color = COLORS.ok;
  else if (status === 'syncing') color = COLORS.warn;
  else if (status === 'error') color = COLORS.danger;
  return { color, fontFamily: FONT.mono, fontSize: FONT.sizeXs };
}
